import React, { useState, useEffect } from 'react';
import { database } from '../../supabase';

interface Post {
  id: string;
  type: 'event' | 'banner' | 'content';
  title?: string;
  description?: string;
  image: string;
  aspect_ratio?: 'square' | 'portrait' | 'landscape' | 'wide';
  position?: number;
  is_active: boolean;
  created_at: string;
}

const PostManager: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [postForm, setPostForm] = useState({
    type: 'event' as 'event' | 'banner' | 'content',
    title: '',
    description: '',
    image: '',
    aspect_ratio: 'square' as 'square' | 'portrait' | 'landscape' | 'wide',
    position: '',
    is_active: true
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await database.getPosts(true);
      if (error) {
        console.warn('Posts table may not exist yet:', error);
        setPosts([]);
      } else {
        setPosts(data || []);
      }
    } catch (err) {
      setError('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPost = () => {
    setEditingPost(null);
    setPostForm({
      type: 'event',
      title: '',
      description: '',
      image: '',
      aspect_ratio: 'square',
      position: '',
      is_active: true
    });
    setShowPostForm(true);
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setPostForm({
      type: post.type,
      title: post.title || '',
      description: post.description || '',
      image: post.image,
      aspect_ratio: post.aspect_ratio || 'square',
      position: post.position?.toString() || '',
      is_active: post.is_active
    });
    setShowPostForm(true);
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await database.deletePost(postId);
      if (error) throw error;
      await loadPosts();
    } catch (err) {
      alert('Failed to delete post');
    }
  };

  const handleSavePost = async () => {
    try {
      const postData = {
        type: postForm.type,
        title: postForm.title || null,
        description: postForm.description || null,
        image: postForm.image,
        aspect_ratio: postForm.aspect_ratio,
        position: postForm.position ? parseInt(postForm.position) : null,
        is_active: postForm.is_active
      };

      if (editingPost) {
        const { error } = await database.updatePost(editingPost.id, postData);
        if (error) throw error;
      } else {
        const { error } = await database.createPost(postData);
        if (error) throw error;
      }

      setShowPostForm(false);
      await loadPosts();
    } catch (err) {
      alert('Failed to save post');
    }
  };

  if (loading) {
    return <div className="loading">Loading posts...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="homepage-section">
      <div className="section-header">
        <h2>Homepage Content Management</h2>
        <button className="add-btn" onClick={handleAddPost}>
          Add Event/Banner
        </button>
      </div>

      <div className="info-box">
        <p><strong>How it works:</strong></p>
        <p>The homepage shows products in groups: 6 small products, then 3 large products, repeating.</p>
        <p>You can add event/banner posts that will appear between these groups. Set the position number to control where they appear (0 = after first group, 1 = after second group, etc.)</p>
      </div>

      <div className="posts-grid">
        {posts.map(post => (
          <div key={post.id} className="post-card">
            <div className="post-image">
              <img
                src={post.image || '/images/placeholder.jpg'}
                alt={post.title || 'Post'}
              />
            </div>
            <div className="post-info">
              <h3>{post.title || 'Untitled'}</h3>
              <p className="post-type">Type: {post.type}</p>
              <p className="post-position">Position: {post.position ?? 'Not set'}</p>
              <p className={`status ${post.is_active ? 'active' : 'inactive'}`}>
                {post.is_active ? 'Active' : 'Inactive'}
              </p>
            </div>
            <div className="post-actions">
              <button onClick={() => handleEditPost(post)}>Edit</button>
              <button
                onClick={() => handleDeletePost(post.id)}
                className="delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div className="empty-state">
            <p>No posts yet. Add an event or banner to get started.</p>
          </div>
        )}
      </div>

      {showPostForm && (
        <div className="modal-overlay" onClick={() => setShowPostForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPost ? 'Edit Post' : 'Add New Post'}</h2>
              <button
                className="close-btn"
                onClick={() => setShowPostForm(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSavePost(); }}>
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={postForm.type}
                    onChange={(e) => setPostForm(prev => ({ ...prev, type: e.target.value as 'event' | 'banner' | 'content' }))}
                    required
                  >
                    <option value="event">Event</option>
                    <option value="banner">Banner</option>
                    <option value="content">Content</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Title (optional, minimal text)</label>
                  <input
                    type="text"
                    value={postForm.title}
                    onChange={(e) => setPostForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Short title (optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Description (optional, minimal text)</label>
                  <textarea
                    value={postForm.description}
                    onChange={(e) => setPostForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    placeholder="Short description (optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Image URL *</label>
                  <input
                    type="url"
                    value={postForm.image}
                    onChange={(e) => setPostForm(prev => ({ ...prev, image: e.target.value }))}
                    required
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Aspect Ratio</label>
                    <select
                      value={postForm.aspect_ratio}
                      onChange={(e) => setPostForm(prev => ({ ...prev, aspect_ratio: e.target.value as 'square' | 'portrait' | 'landscape' | 'wide' }))}
                    >
                      <option value="square">Square (1:1)</option>
                      <option value="portrait">Portrait (2:3)</option>
                      <option value="landscape">Landscape (3:2)</option>
                      <option value="wide">Wide (16:9)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Position (0 = after first group, 1 = after second, etc.)</label>
                    <input
                      type="number"
                      value={postForm.position}
                      onChange={(e) => setPostForm(prev => ({ ...prev, position: e.target.value }))}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={postForm.is_active}
                      onChange={(e) => setPostForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    Active (show on homepage)
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setShowPostForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="save-btn">
                    {editingPost ? 'Update Post' : 'Create Post'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostManager;
